import { useLayoutEffect, useRef } from 'react';

import { useDispatch, useSelector } from 'store';
import { setFloatingAiVisibility } from 'store/reducers/aiChat';
import { closeSubDrawer } from 'store/reducers/subdrawer';
import { closeTemporaryDrawer } from 'store/reducers/temporaryDrawer';
import { INBOX_DRAWER_ID } from './Inbox';
import { useJoinedPanels } from './joinedPanels';
import { TIMECARD_CENTER_DRAWER_ID } from './TopBar/TimerControl';

/**
 * Keeps one side panel open at a time: as the AI panel, the Inbox, the Timecard center or a
 * joined panel opens, the others that are open close. It watches the open states rather than their openers,
 * so every way of opening a panel is covered, and one opened again while already open closes nothing. A panel
 * whose open flag is not in the store joins through `useJoinedPanel`.
 */
export function useOnePanelOpen() {
    const dispatch = useDispatch();
    const ai = useSelector((state) => state.aiChat.floatingVisibility === 'open');
    const temporaryId = useSelector((state) => state.temporaryDrawer.activeDrawerId);
    const subId = useSelector((state) => state.subDrawer.activeDrawerId);
    const inbox = temporaryId === INBOX_DRAWER_ID;
    const timecard = subId === TIMECARD_CENTER_DRAWER_ID;
    const joined = useJoinedPanels();
    const wasOpen = useRef<unknown[]>([]);

    // A layout effect: the others start closing in the frame the panel starts opening, with no paint of
    // two open panels in between.
    useLayoutEffect(() => {
        // Each with the way it closes itself, so its exit and its focus handling run: the AI panel goes
        // through `closing`, which its host turns into `closed` once the exit has played. In the order of
        // which one stays when several open in the same render, as on mount or as the tour ends. A joined
        // panel is listed only while open, under its close.
        const open = [
            { id: 'ai', isOpen: ai, close: () => dispatch(setFloatingAiVisibility('closing')) },
            { id: 'inbox', isOpen: inbox, close: () => dispatch(closeTemporaryDrawer()) },
            { id: 'timecard', isOpen: timecard, close: () => dispatch(closeSubDrawer()) },
            ...joined.map((close) => ({ id: close, isOpen: true, close })),
        ].filter((panel) => panel.isOpen);
        const previous = wasOpen.current;
        wasOpen.current = open.map((panel) => panel.id);
        const opened = open.find((panel) => !previous.includes(panel.id));
        if (!opened) return;
        open.forEach((panel) => {
            if (panel !== opened) panel.close();
        });
    }, [dispatch, ai, inbox, timecard, joined]);

    // A classic drawer left open across the switch to this design, Notifications or Tasks in the Inbox's slot
    // or the filter panel in the Timecard center's: nothing renders into it here, and its panel would show
    // as an empty box.
    useLayoutEffect(() => {
        if (temporaryId && !inbox) dispatch(closeTemporaryDrawer());
        if (subId && !timecard) dispatch(closeSubDrawer());
    }, [dispatch, temporaryId, inbox, subId, timecard]);
}
